import type { SortingState } from "@tanstack/react-table";
import type { FeatureCollection, Geometry } from "geojson";
import { atomFamily, atomWithStorage } from "jotai/utils";
import type { SyncStorage } from "jotai/vanilla/utils/atomWithStorage";
import type {
    AdminEmissionsRecordsStatsListProviderWithSourceParameterInner,
    InfrastructureSitesListEmissionStatsParameter,
} from "../../apiClient/generated";
import { InfrastructureSitesListEmissionStatsParameterToJSON } from "../../apiClient/generated";

export type TableDateFilterState = {
    type: "date";
    after?: Date;
    before?: Date;
};

export type RangeFilterState = {
    type: "range";
    min?: number;
    max?: number;
};

export type LocationFilterState = {
    type: "location";
    geometry?: FeatureCollection;
    publicPresets?: number[];
};

export type SiteEmissionsStats = {
    type: "emissionStats";
    value: InfrastructureSitesListEmissionStatsParameter;
};

function featureCollectionToMultiPolygonString(
    fc: FeatureCollection,
): string | undefined {
    const coordinates: number[][][][] = [];

    const collectPolygons = (geometry: Geometry): void => {
        switch (geometry.type) {
            case "Polygon":
                coordinates.push(geometry.coordinates);
                break;
            case "MultiPolygon":
                coordinates.push(...geometry.coordinates);
                break;
            case "GeometryCollection":
                geometry.geometries.forEach(collectPolygons);
                break;
            // ignore Point, LineString, MultiPoint, MultiLineString
        }
    };

    for (const feature of fc.features) {
        collectPolygons(feature.geometry);
    }

    if (coordinates.length === 0) return undefined;
    if (coordinates.length === 1) {
        return JSON.stringify({ type: "Polygon", coordinates: coordinates[0] });
    }
    return JSON.stringify({ type: "MultiPolygon", coordinates });
}

export type ProviderFilterState = {
    type: "provider";
    value: AdminEmissionsRecordsStatsListProviderWithSourceParameterInner[];
};

export interface FilterState {
    [key: string]:
        | string
        | string[]
        | number
        | TableDateFilterState
        | LocationFilterState
        | RangeFilterState
        | ProviderFilterState
        | SiteEmissionsStats;
}

export interface TableState {
    /**
     * Filters
     *
     * To be stored exactly as needed by the API endpoints.
     * Can be manipulated freely.
     */
    filters: FilterState;
    /**
     * Sorting state.
     *
     * Either undefined (default API sorting), or a combination
     * of sorting key + if it's ascending or descending order.
     *
     */
    sorting?: SortingState;
    /**
     * Pagination
     *
     * Can be controlled from anywhere in the page.
     * Frontend pagination starts at page 0, backend pagination starts from page 1.
     *
     * TODO: fix pagination bug when filters are selected.
     */
    pagination: {
        pageIndex: number;
        pageSize: number;
    };
    /**
     * Column visibility
     *
     * Allows customizing which columns are visible in the table,
     * using the `id` identifier.
     */
    columnVisibility?: {
        [key: string]: boolean;
    };
    /**
     * Row selection state
     */
    rowSelection: {
        [key: string]: boolean;
    };
}

interface TableStateFamily {
    // Per-instance id, keys the atom family and the `table-<tableId>` storage.
    tableId: string;
    // Config id, keys the `config-<configId>` storage. Defaults to tableId.
    // Share it across instances to centralize filters/sorting/columns.
    configId?: string;
    initialState?: Partial<TableState>;
}

// Revives Date objects from ISO strings when deserializing the durable part
// of TableState (e.g. date filter after/before fields).
const dateReviver = (_key: string, value: unknown): unknown => {
    if (
        typeof value === "object" &&
        value !== null &&
        "type" in value &&
        (value as { type?: unknown }).type === "date"
    ) {
        const { type, after, before } = value as {
            type: "date";
            after?: string;
            before?: string;
        };
        return {
            type,
            after: after ? new Date(after) : undefined,
            before: before ? new Date(before) : undefined,
        };
    }
    return value;
};

// Config persists under `config-<configId>`, pagination under `table-<tableId>`.
// rowSelection is not persisted, so a reload clears it.
const CONFIG_KEYS = ["filters", "sorting", "columnVisibility"] as const;
const VIEW_KEYS = ["pagination"] as const;

const pick = (obj: Partial<TableState>, keys: readonly (keyof TableState)[]) =>
    Object.fromEntries(
        keys.filter((k) => k in obj).map((k) => [k, obj[k]]),
    ) as Partial<TableState>;

const parseStored = (
    raw: string | null,
    reviver?: (key: string, value: unknown) => unknown,
): Partial<TableState> => {
    if (!raw) return {};
    try {
        return JSON.parse(raw, reviver);
    } catch {
        // Ignore malformed storage and fall back to defaults.
        return {};
    }
};

// The atomWithStorage key encodes both ids as `configId::tableId`. This custom
// storage splits it and routes each subset to its own localStorage key.
const tableStateStorage: SyncStorage<TableState> = {
    getItem: (key, initialValue) => {
        const [configId, tableId] = key.split("::");
        return {
            ...initialValue,
            ...pick(
                parseStored(
                    localStorage.getItem(`config-${configId}`),
                    dateReviver,
                ),
                CONFIG_KEYS,
            ),
            ...pick(
                parseStored(localStorage.getItem(`table-${tableId}`)),
                VIEW_KEYS,
            ),
        };
    },
    setItem: (key, value) => {
        const [configId, tableId] = key.split("::");
        localStorage.setItem(
            `config-${configId}`,
            JSON.stringify(pick(value, CONFIG_KEYS)),
        );
        localStorage.setItem(
            `table-${tableId}`,
            JSON.stringify(pick(value, VIEW_KEYS)),
        );
    },
    removeItem: (key) => {
        const [configId, tableId] = key.split("::");
        localStorage.removeItem(`config-${configId}`);
        localStorage.removeItem(`table-${tableId}`);
    },
};

// Generic atom that allows us to generate custom table
// states using the same basic structure.
export const tableStateFamily = atomFamily(
    ({ initialState, tableId, configId }: TableStateFamily) =>
        atomWithStorage<TableState>(
            `${configId ?? tableId}::${tableId}`,
            {
                filters: {},
                sorting: undefined,
                pagination: {
                    pageIndex: 0,
                    pageSize: 10,
                },
                rowSelection: {},
                ...initialState,
            },
            tableStateStorage,
            {
                getOnInit: true,
            },
        ),
    (a, b) => a.tableId === b.tableId,
);

export const filterStateToApiParams = (filterState: FilterState) => {
    const apiFilterValues = {};
    for (const [key, value] of Object.entries(filterState)) {
        // If the value is an object, check which filter state is is and
        // perform the appropriate conversions.
        if (
            typeof value === "object" &&
            !Array.isArray(value) &&
            value !== null
        ) {
            // Handle date types
            if (value.type === "date") {
                apiFilterValues[`${key}Before`] = value.before;
                apiFilterValues[`${key}After`] = value.after;
            }

            // Handle location filter (fixme: make keys configurable)
            if (value.type === "location") {
                apiFilterValues["locationWithin"] = value.geometry
                    ? featureCollectionToMultiPolygonString(value.geometry)
                    : undefined;
                apiFilterValues["locationPreset"] = value.publicPresets;
            }

            // Handle numeric range values
            if (value.type === "range") {
                apiFilterValues[`${key}Min`] = value.min;
                apiFilterValues[`${key}Max`] = value.max;

                // FIXME: Workaround for detectedRate fields being in grams
                if (key === "detectedRate") {
                    apiFilterValues[`${key}Min`] = value.min
                        ? value.min * 1000
                        : undefined;
                    apiFilterValues[`${key}Max`] = value.max
                        ? value.max * 1000
                        : undefined;
                }
            }

            // Handle provider filter
            if (value.type === "provider") {
                apiFilterValues["providerWithSource"] = JSON.stringify(
                    value.value,
                );
            }

            if (value.type === "emissionStats") {
                apiFilterValues["emissionStats"] = JSON.stringify(
                    InfrastructureSitesListEmissionStatsParameterToJSON(
                        value.value,
                    ),
                );
            }
        } else {
            // Else add normally to array.
            apiFilterValues[key] = value;
        }
    }
    return apiFilterValues;
};
